import type { useErrorToast } from "@v2/hooks/useErrorToast";

import { isFirefox, isSafari } from "@replo/design-system/utils/platform";
import { setStorageItem } from "@v2/hooks/useLocalStorage";

const ReploComponentClipboardKey = "alchemy-component-clipboard";

const textHtmlType = "text/html";
const textPlainType = "text/plain";

// Note: Ovishek (2022-04-08) escape/unescape is needed to encode the html tags and quotation marks
// we are not using encode/decode URI functions b/c text can already have encoded stuff that we don't want
// to expose during decoding process. For more context, https://linear.app/replo/issue/REPL-1567
export const getHtmlFromJsonString = (text: string) => {
  return `<div data-alchemy-clipboard="${escape(text)}"></div>`;
};

/**
 * Internal helper that writes content to clipboard with proper permissions checking.
 *
 * @param content - Content to write (can be raw HTML or JSON-wrapped)
 * @param wrapInJson - Whether to wrap content in the special Replo JSON format
 * @param errorToast - Error toast function from useErrorToast hook
 * @param safariFirefoxFallback - Optional plain text fallback for Safari/Firefox
 */
export const writeToClipboardInternal = async (
  content: string,
  wrapInJson: boolean,
  errorToast: ReturnType<typeof useErrorToast>,
  safariFirefoxFallback?: string,
) => {
  const doesSupportClipboardWrite = Boolean(navigator.clipboard.write);

  if (wrapInJson) {
    // Store in localStorage for legacy compatibility
    setStorageItem(ReploComponentClipboardKey, content);
  }

  if (!doesSupportClipboardWrite) {
    errorToast(
      "Clipboard Not Supported",
      "Your browser doesn't support clipboard operations.",
      "error.clipboard.write",
    );
    return;
  }

  if (!isFirefox() && !isSafari()) {
    const clipboardWritePermission = await navigator?.permissions?.query({
      // @ts-expect-error clipboard-write is valid but not in TS types
      name: "clipboard-write",
    });

    const hasWritePermission =
      clipboardWritePermission &&
      ["granted", "prompt"].includes(clipboardWritePermission.state);

    if (!hasWritePermission) {
      errorToast(
        "Missing Permissions",
        "Replo requires permission to write to the clipboard. You can grant this permission in your browser's settings.",
        "error.clipboard.write",
        {
          permissions: navigator?.permissions,
        },
      );
      return;
    }

    const htmlContent = wrapInJson ? getHtmlFromJsonString(content) : content;

    try {
      const htmlBlob = new Blob([htmlContent], { type: textHtmlType });
      const textBlob = new Blob([safariFirefoxFallback ?? content], {
        type: textPlainType,
      });
      const data = [
        new ClipboardItem({
          [textHtmlType]: htmlBlob,
          [textPlainType]: textBlob,
        }),
      ];
      await navigator.clipboard.write(data);
    } catch (htmlError) {
      console.warn(
        "HTML clipboard write failed, using text fallback:",
        htmlError,
      );
      try {
        await navigator.clipboard.writeText(safariFirefoxFallback ?? content);
      } catch (textError) {
        errorToast(
          "Clipboard Error",
          "Failed to write to clipboard. Please try again.",
          "error.clipboard.write",
          { error: String(textError) },
        );
        throw textError;
      }
    }
  } else if (safariFirefoxFallback) {
    await navigator.clipboard.writeText(safariFirefoxFallback);
  } else {
    errorToast(
      "Clipboard Error",
      "Unable to write to clipboard on this browser.",
      "error.clipboard.write",
    );
    return;
  }
};

/**
 * Writes raw HTML content to the clipboard with proper error handling.
 * Use this for writing user-facing HTML content (like chat messages) rather than
 * internal Replo component data.
 *
 * All errors are handled internally with error toasts - this function never throws.
 *
 * @param html - Raw HTML string to write to clipboard
 * @param errorToast - Error toast function from useErrorToast hook
 * @returns Promise that resolves to true on success, false on failure
 */
export const writeHtmlToClipboard = async (
  html: string,
  errorToast: ReturnType<typeof useErrorToast>,
): Promise<boolean> => {
  try {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const plainText = tempDiv.textContent ?? "";

    await writeToClipboardInternal(html, false, errorToast, plainText);
    return true;
  } catch (error) {
    console.error("Clipboard write error:", error);
    return false;
  }
};
